import type { FieldSet, Record as AirtableRecord, RecordData } from '@rogoag/airtable/crm';
import chalk from 'chalk';
import logger from './logger';
import { logColor } from './utils';
import { Airtable as CRM, TableName as CrmTableName } from '@rogoag/airtable/crm';

/**
 * Mirrors the subset of the package's `FetchOptions` that `select` forwards to
 * `table.get`. Declared here because @rogoag/airtable v3 does not re-export
 * FetchOptions from its `crm` entry point and its `exports` map blocks the deep
 * import that used to reach it.
 */
export type SelectOptions<FieldSet> = {
  /** Records per API page (Airtable defaults to 100) */
  pageSize?: number;
  /** Restrict which fields are returned */
  fields?: (keyof FieldSet)[];
  /** Return field keys as IDs (fldXXX) instead of names */
  useFieldIds?: boolean;
  /** Cap the total number of records returned */
  maxRecords?: number;
  offset?: number;
  timeZone?: string;
  userLocale?: string;
};

const LOG_API_CALL_DURATION_TO_DYNAMO = true;

class AirtableCrud {
  baseId: string;
  defaultFields: Record<string, string[]>;
  private api: CRM;

  constructor(baseId: string, apiKey: string, defaultFields: Record<string, string[]>) {
    this.baseId = baseId;
    this.defaultFields = defaultFields;
    this.api = new CRM({ baseId, apiKey });
  }

  private getTable(tableName: string) {
    return this.api.table(tableName as CrmTableName);
  }

  async create<T extends FieldSet>(tableName: string, fields: T): Promise<AirtableRecord<T>> {
    const start = performance.now();

    this.remoteAirtableAPICall();
    console.count(`AirtableCrud::create (tableName=${tableName})`);

    const table = this.getTable(tableName);
    // @ts-ignore - typed API returns compatible record format
    const result = await table.create(fields as Record<string, unknown>);

    const duration = performance.now() - start;

    if (LOG_API_CALL_DURATION_TO_DYNAMO) {
      await logger.log(`AT call - create - finished in ${duration.toFixed(0)} ms`, { tableName, fields });
    }

    console.log(chalk.hex(logColor(duration))(`AirtableCrud::create - finished in ${duration.toFixed(0)} ms`), {
      tableName,
      fields,
    });

    return result as unknown as AirtableRecord<T>;
  }

  async find<T extends FieldSet = FieldSet>(tableName: string, recordId: string): Promise<AirtableRecord<T>> {
    const start = performance.now();

    this.remoteAirtableAPICall();
    console.count(`AirtableCrud::find (tableName=${tableName}, recordId=${recordId})`);

    const table = this.getTable(tableName);
    // @ts-ignore - typed API returns compatible record format
    const result = await table.get(recordId, { returnAs: 'record' });

    const duration = performance.now() - start;

    if (LOG_API_CALL_DURATION_TO_DYNAMO) {
      await logger.log(`AT call - find - finished in ${duration.toFixed(0)} ms`, { tableName, recordId });
    }

    console.log(chalk.hex(logColor(duration))(`AirtableCrud::find - finished in ${duration.toFixed(0)} ms`), {
      tableName,
      recordId,
    });

    return result as unknown as AirtableRecord<T>;
  }

  async select<T extends FieldSet = FieldSet>(
    tableName: string,
    viewName: string,
    query: string,
    options: SelectOptions<FieldSet> = {},
  ): Promise<AirtableRecord<T>[]> {
    const start = performance.now();

    const selectOptions: Record<string, unknown> = { ...options };

    if (tableName in this.defaultFields) {
      selectOptions.fields = this.defaultFields[tableName];
    }

    if (viewName) {
      selectOptions.view = viewName;
    }

    if (query) {
      selectOptions.formula = query;
    }

    this.remoteAirtableAPICall();
    console.count(`AirtableCrud::select ${tableName}`);

    const table = this.getTable(tableName);
    // @ts-ignore - typed API returns compatible record format
    const result = await table.get({ ...selectOptions, returnAs: 'record' });

    const duration = performance.now() - start;

    if (LOG_API_CALL_DURATION_TO_DYNAMO) {
      await logger.log(`AT call - select - finished in ${duration.toFixed(0)} ms`, {
        tableName,
        viewName,
        shortQuery: query.substring(0, 100),
      });
    }

    console.log(chalk.hex(logColor(duration))(`AirtableCrud::select - finished in ${duration.toFixed(1)} ms`), {
      tableName,
      viewName,
      shortQuery: query.substring(0, 100),
    });

    return result as unknown as AirtableRecord<T>[];
  }

  async update<T extends FieldSet>(tableName: string, recordsToUpdate: RecordData<T>[]): Promise<AirtableRecord<T>[]> {
    const start = performance.now();

    this.remoteAirtableAPICall();
    console.count(`AirtableCrud::update ${tableName}`);

    const recordsForUpdate = recordsToUpdate.map((r) => ({
      id: r.id,
      fields: r.fields as Record<string, unknown>,
    }));

    const table = this.getTable(tableName);
    // @ts-ignore - typed API returns compatible record format
    const result = await table.update(recordsForUpdate);

    const duration = performance.now() - start;

    if (LOG_API_CALL_DURATION_TO_DYNAMO) {
      await logger.log(`AT call - update - finished in ${duration.toFixed(0)} ms`, {
        tableName,
        recordsIds: recordsToUpdate.map((r) => r.id),
      });
    }

    console.log(chalk.hex(logColor(duration))(`AirtableCrud::update - finished in ${duration.toFixed(0)} ms`), {
      tableName,
      recordsIds: recordsToUpdate.map((r) => r.id),
    });

    return result as unknown as AirtableRecord<T>[];
  }

  private remoteAirtableAPICall() {
    console.count('AirtableCrud::Remote airtable API call');
  }
}

export default AirtableCrud;
